<template>
    <div class="container-nzidoo">
        <page-title title="sidebar.boosterPlansManagement" />

        <!-- Toolbar -->
        <div class="admin-toolbar">
            <div class="admin-search-box">
                <i class="mdi mdi-magnify admin-search-icon"></i>

                <input type="text" v-model="searchTerm" :placeholder="$t('sentence.searchByName')"
                    class="admin-search-input">

                <!-- Clear icon -->
                <i v-if="searchTerm" class="mdi mdi-close-circle admin-clear-icon" @click="clearSearch"
                    :title="$t('sentence.clear')"></i>
            </div>

            <button class="btn btn-primary" @click="openCreateModal">
                <i class="mdi mdi-plus"></i>
                <div class="d-none d-md-block">
                    {{ $t("action.add") }}
                </div>
            </button>
        </div>

        <LoadingSpinner v-if="loader" />
        <!-- Booster Plans Grid -->
        <div class="admin-grid">
            <div v-for="plan in filteredPlans" :key="plan.id" class="admin-card">
                <div class="admin-card-header">
                    <div class="plan-header-content">
                        <span class="d-flex card-name mdi mdi-sort-ascending">{{ plan.sort_order }}</span>
                        <span class="card-name">{{ plan.name }}</span>
                    </div>
                    <div class="admin-card-actions">
                        <button class="btn btn-icon-action btn-warning" @click="edit(plan.id)"
                            :title="$t('action.edit')">
                            <i class="mdi mdi-pencil"></i>
                        </button>
                        <button class="btn btn-icon-action btn-danger" @click="remove(plan.id)"
                            :title="$t('action.delete')">
                            <i class="mdi mdi-delete"></i>
                        </button>
                    </div>
                </div>
                <div class="admin-card-body">
                    <div class="admin-card-section">
                        <label class="admin-card-label">{{ $t('attr.description') }}</label>
                        <div class="card-light">{{ plan.description }}</div>
                    </div>
                    <div class="details-row">
                        <div class="admin-card-section">
                            <label class="admin-card-label">{{ $t('attr.duration') }}</label>
                            <div class="card-name">{{ plan.duration_days }} {{ $t('attr.days') }}</div>
                        </div>
                        <div class="admin-card-section">
                            <label class="admin-card-label">{{ $t('attr.price') }}</label>
                            <div class="card-name price-text">{{ formatPrice(plan.price) }}</div>
                        </div>
                        <div class="admin-card-section">
                            <label class="admin-card-label">{{ $t('general.type') }}</label>
                            <div class="card-name">{{ $t(`boosterPlans.type.${plan.type}`) }}</div>
                        </div>
                    </div>
                    <div class="admin-card-section text-center mt-2">
                        <div :class="['card-name ', plan.is_active ? 'status-active' : 'status-inactive']">{{
                            plan.is_active ? $t('status.active') : $t('status.inactive') }}</div>
                    </div>
                </div>
            </div>

            <!-- Empty State -->
            <div v-if="filteredPlans.length === 0" class="admin-empty-state">
                <svg class="admin-empty-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <circle cx="11" cy="11" r="8"></circle>
                    <path d="m21 21-4.35-4.35"></path>
                </svg>
                <p class="admin-empty-text">{{ $t('sentence.noResults') }}</p>
            </div>
        </div>

        <!-- Drawer Backdrop -->
        <transition name="drawer-backdrop">
            <div v-if="showModal" class="admin-drawer-backdrop" @click="closeModal"></div>
        </transition>

        <!-- Drawer -->
        <transition name="drawer">
            <div v-if="showModal" class="admin-drawer-container">
                <div class="admin-drawer-header">
                    <h3 class="admin-drawer-title">
                        {{ isEditing ? $t('boosterPlans.editPlan') : $t('boosterPlans.addPlan') }}
                    </h3>
                    <button class="btn btn-close" @click="closeModal">
                        <i class="mdi mdi-close"></i>
                    </button>
                </div>

                <div class="admin-drawer-body">
                    <div class="admin-form-group">
                        <label class="admin-form-label">{{ $t('attr.name') }} *</label>
                        <input type="text" v-model="form.name" class="admin-form-input" :placeholder="$t('attr.name')">
                    </div>

                    <div class="admin-form-group">
                        <label class="admin-form-label">{{ $t('attr.description') }} *</label>
                        <textarea v-model="form.description" class="admin-form-textarea" rows="4"
                            :placeholder="$t('attr.description')"></textarea>
                    </div>

                    <div class="form-row">
                        <div class="admin-form-group">
                            <label class="admin-form-label">{{ $t('attr.duration') }} *</label>
                            <input type="number" v-model.number="form.duration_days" class="admin-form-input" min="1"
                                :placeholder="$t('attr.duration')">
                        </div>

                        <div class="admin-form-group">
                            <label class="admin-form-label">{{ $t('attr.price') }} *</label>
                            <input type="number" v-model.number="form.price" class="admin-form-input" min="0"
                                step="0.01" :placeholder="$t('attr.price')">
                        </div>
                    </div>

                    <div class="form-row">
                        <div class="admin-form-group">
                            <label class="admin-form-label">{{ $t('general.type') }} *</label>
                            <select v-model="form.type" class="admin-form-input">
                                <option value="">{{ $t('action.selecteOne') }}</option>
                                <option value="boost_ad">{{ $t('boosterPlans.type.boost_ad') }}</option>
                                <option value="top_visibility">{{ $t('boosterPlans.type.top_visibility') }}</option>
                                <option value="urgent_ad">{{ $t('boosterPlans.type.urgent_ad') }}</option>
                            </select>
                        </div>

                        <div class="admin-form-group">
                            <label class="admin-form-label">{{ $t('attr.sortOrder') }}</label>
                            <input type="number" v-model.number="form.sort_order" class="admin-form-input" min="0"
                                :placeholder="$t('attr.sortOrder')">
                            <span class="admin-form-hint">{{ $t('attr.sortOrder') }}</span>
                        </div>
                    </div>

                    <div class="admin-form-group">
                        <label class="admin-form-label">{{ $t('main.status') }}</label>
                        <div class="toggle-container">
                            <label class="toggle-switch">
                                <input type="checkbox" v-model="form.is_active">
                                <span class="toggle-slider"></span>
                            </label>
                            <span class="toggle-label">
                                {{ form.is_active ? $t('status.active') : $t('status.inactive') }}
                            </span>
                        </div>
                    </div>
                </div>

                <div class="admin-drawer-footer">
                    <button class="btn btn-secondary" @click="closeModal">
                        {{ $t('action.cancel') }}
                    </button>
                    <button class="btn btn-primary" @click="save" :disabled="!isFormValid">
                        {{ isEditing ? $t('action.update') : $t('action.create') }}
                    </button>
                </div>
            </div>
        </transition>

        <notifications style="top:5%;" width="35%" group="error" position="top center" />
        <notifications style="top:5%;" width="35%" group="success" position="top center" />
    </div>
</template>

<script>
import PageTitle from '@/components/templates/PageTitle.vue';
import AlertService from '@/services/alertService';
import helper from "@/services/helper";
import LoadingSpinner from "@/components/templates/LoadingSpinner.vue";

export default {
    components: { PageTitle, LoadingSpinner },
    name: "BoosterPlans",

    data() {
        return {
            plans: [],
            showModal: false,
            isEditing: false,
            editId: null,
            searchTerm: "",
            loader: false,
            form: {
                name: "",
                description: "",
                duration_days: null,
                price: null,
                sort_order: 0,
                is_active: true
            }
        };
    },

    computed: {
        filteredPlans() {
            if (!this.searchTerm) return this.plans;

            const term=this.searchTerm.toLowerCase();
            return this.plans.filter(plan =>
                plan.name.toLowerCase().includes(term)||
                plan.description.toLowerCase().includes(term)||
                plan.duration_days.toString().includes(term)
            );
        },

        isFormValid() {
            return this.form.name&&
                this.form.description&&
                this.form.duration_days>0&&
                this.form.price>=0;
        }
    },

    mounted() {
        this.loadPlans();
    },

    methods: {
        clearSearch() {
            this.searchTerm="";
        },
        formatPrice: helper.formatPrice,
        async loadPlans() {
            try {
        this.loader=true;
                const res=await this.$axios.get("/admin/booster-plans");
                this.plans=Array.isArray(res.data.data)
                    ? res.data.data
                    :Object.values(res.data.data);

        this.loader=false;
            } catch (err) {
                console.error("Error loading booster plans:", err);
                this.$notify({
                    group: "error",
                    title: this.$t('sentence.errorLoading'),
                    type: "error",
                });
            }
        },

        openCreateModal() {
            this.isEditing=false;
            this.showModal=true;
            this.form={
                name: "",
                description: "",
                duration_days: null,
                price: null,
                sort_order: 0,
                is_active: true
            };
        },

        closeModal() {
            this.showModal=false;
        },

        async save() {
            if (!this.isFormValid) return;

            try {
                if (this.isEditing) {
                    const res=await this.$axios.put(`/admin/booster-plans/${this.editId}`, this.form);
                    this.$notify({
                        group: "success",
                        title: res.data.message||this.$t('sentence.updateSuccess'),
                        type: "success",
                    });
                } else {
                    const res=await this.$axios.post("/admin/booster-plans", this.form);
                    this.$notify({
                        group: "success",
                        title: res.data.message||this.$t('sentence.createSuccess'),
                        type: "success",
                    });
                }
                this.closeModal();
                this.loadPlans();
            } catch (err) {
                console.error("Error saving booster plan:", err);
                this.$notify({
                    group: "error",
                    title: err.response?.data?.message||this.$t('sentence.errorSaving'),
                    type: "error",
                });
            }
        },

        async edit(id) {
            this.isEditing=true;
            this.editId=id;

            try {
                const res=await this.$axios.get(`/admin/booster-plans/${id}`);
                this.form={ ...res.data };
                this.showModal=true;
            } catch (err) {
                console.error("Error loading booster plan:", err);
                this.$notify({
                    group: "error",
                    title: err.response?.data?.message||this.$t('sentence.errorLoading'),
                    type: "error",
                });
            }
        },

        async remove(id) {
            const confirmed=await AlertService.confirm({
                title: this.$t('sentence.confirmDelete')||"Are you sure?",
                confirmButtonText: this.$t('action.delete')||"Delete",
                cancelButtonText: this.$t('action.cancel')||"Cancel",
                icon: "warning"
            });

            if (!confirmed) return;

            try {
                const res=await this.$axios.delete(`/admin/booster-plans/${id}`);
                this.$notify({
                    group: "success",
                    title: res.data.message||this.$t('sentence.deleteSuccess'),
                    type: "success",
                });
                this.loadPlans();
            } catch (err) {
                console.error("Error deleting booster plan:", err);
                this.$notify({
                    group: "error",
                    title: err.response?.data?.message||this.$t('sentence.errorDeleting'),
                    type: "error",
                });
            }
        }
    }
};
</script>

<style scoped src="@/assets/css/shared-admin-styles.css"></style>
<style scoped>

.card-name {
    font-size: 8pt;
}

/* Component-specific styles */
.plan-header-content {
    display: flex;
    align-items: center;
    gap: 12px;
    flex: 1;
}

.status-badge {
    padding: 4px 12px;
    border-radius: 12px;
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.status-active {
    background-color: #dcfce7;
    color: #166534;
}

.status-inactive {
    background-color: #fee2e2;
    color: #991b1b;
}

.details-row {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
    margin-top: 8px;
}

.price-text {
    color: var(--primary-color, #3b82f6);
    font-weight: 600;
}

.form-row {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
}

.toggle-container {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-top: 8px;
}

.toggle-switch {
    position: relative;
    display: inline-block;
    width: 50px;
    height: 26px;
}

.toggle-switch input {
    opacity: 0;
    width: 0;
    height: 0;
}

.toggle-slider {
    position: absolute;
    cursor: pointer;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: #ccc;
    transition: 0.3s;
    border-radius: 26px;
}

.toggle-slider:before {
    position: absolute;
    content: "";
    height: 20px;
    width: 20px;
    left: 3px;
    bottom: 3px;
    background-color: white;
    transition: 0.3s;
    border-radius: 50%;
}

.toggle-switch input:checked+.toggle-slider {
    background-color: var(--primary-color, #3b82f6);
}

.toggle-switch input:checked+.toggle-slider:before {
    transform: translateX(24px);
}

.toggle-label {
    font-size: 14px;
    font-weight: 500;
    color: var(--gray-700);
}

@media (max-width: 768px) {
    .details-row {
        grid-template-columns: 1fr;
    }

    .form-row {
        grid-template-columns: 1fr;
    }
}
</style>